import moment from "moment-timezone";
import { VerifyCheckResponse } from "client/openapi";
import { EMBED_PATH, inEmbed } from "util/embed";
import { isWhiteLabelHost } from "util/whiteLabel";

/**
 * A tutor's white-label link: "Mary-Jane O'Neil" is "mary-jane-o-neil".
 * The server resolves links the same way.
 */
export function slugify(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

type Named = { id: number; first_name: string; last_name: string };

/**
 * A tutor's page: by name on an org's own domain, unless two share it or
 * the name has no letters a to z. A subject picked on the org page comes
 * along. In a widget, the widget's own page for the tutor.
 */
export function tutorHref(
  tutor: Named,
  listed: Named[],
  subjectId?: number
): string {
  const query = subjectId === undefined ? "" : `?subject=${subjectId}`;
  if (inEmbed()) return `${EMBED_PATH}/tutors/${tutor.id}${query}`;
  const slug = slugify(tutor.first_name, tutor.last_name);
  const byName =
    isWhiteLabelHost() &&
    slug !== "" &&
    listed.filter((t) => slugify(t.first_name, t.last_name) === slug).length ===
      1;
  return (byName ? `/${slug}` : `/tutors/${tutor.id}`) + query;
}

/**
 * The org page: the domain's root on an org's own domain. In a widget, the
 * widget's tutor list.
 */
export function orgHref(orgId: number): string {
  if (inEmbed()) return `${EMBED_PATH}/organizations/${orgId}`;
  return isWhiteLabelHost() ? "/" : `/organizations/${orgId}`;
}

/** The org page's Classes tab (in a widget, the widget's class list). */
export function classesHref(orgId: number): string {
  if (inEmbed()) return `${EMBED_PATH}/organizations/${orgId}/classes`;
  return isWhiteLabelHost() ? "/classes" : `/organizations/${orgId}/classes`;
}

/** The accent color at a light strength, for open days and chips. */
export function tint(hex: string, alpha = "1F"): string {
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? `${hex}${alpha}` : hex;
}

export const lengthLabel = (minutes: number) => `${minutes} min`;

export function timeLabel(iso: string | Date, zone: string): string {
  return moment.tz(iso, zone).format("h:mma");
}

/** "Eastern Time" for America/New_York, or the zone's id. */
export function zoneLabel(zone: string): string {
  try {
    const name = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      timeZoneName: "longGeneric",
    } as Intl.DateTimeFormatOptions)
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value;
    return name || zone;
  } catch {
    return zone;
  }
}

/** "(617) 555-0142" for a US number, else the number as typed. */
export function phoneLabel(phone: string): string {
  const digits = phone.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  return digits.length === 10
    ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
    : phone;
}

/**
 * A checked phone stays checked for 30 minutes in this tab, so "Book another
 * session" needs no second code. The server holds the real expiry.
 */
export type Verified = VerifyCheckResponse & { phone: string; at: number };
const VERIFIED_FOR = 29 * 60 * 1000;

export function loadVerified(orgId: number): Verified | undefined {
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(`booking-verified:${orgId}`) || "null"
    ) as Verified | null;
    return saved && Date.now() - saved.at < VERIFIED_FOR ? saved : undefined;
  } catch {
    return undefined;
  }
}

export function saveVerified(orgId: number, verified?: Verified) {
  try {
    const key = `booking-verified:${orgId}`;
    if (verified) sessionStorage.setItem(key, JSON.stringify(verified));
    else sessionStorage.removeItem(key);
  } catch {
    // Private windows may refuse storage; the code is then asked again.
  }
}

type CalendarEvent = {
  title: string;
  start: string;
  minutes: number;
  location: string;
  details: string;
};

const stamp = (date: moment.Moment) =>
  date.clone().utc().format("YYYYMMDD[T]HHmmss[Z]");

/**
 * An .ics content line folded at 75 octets (RFC 5545): longer lines go on
 * as continuation lines that start with a space. Never splits a character.
 */
function fold(line: string): string {
  const bytes = (text: string) => new TextEncoder().encode(text).length;
  const parts: string[] = [];
  let current = "";
  for (const char of Array.from(line)) {
    const limit = parts.length ? 74 : 75; // continuation lines start with a space
    if (bytes(current + char) > limit) {
      parts.push(current);
      current = "";
    }
    current += char;
  }
  parts.push(current);
  return parts.join("\r\n ");
}

/**
 * Links that add the session to Google, Apple (.ics), Outlook.com and
 * Outlook for work or school (Microsoft 365) calendars.
 */
export function calendarLinks(event: CalendarEvent) {
  const start = moment(event.start);
  const end = start.clone().add(event.minutes, "minutes");
  const google = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${stamp(start)}/${stamp(end)}`,
    details: event.details,
    location: event.location,
  });
  const microsoft = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: event.title,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    body: event.details,
    location: event.location,
  });
  const escape = (text: string) =>
    text.replace(/[\\;,]/g, (c) => `\\${c}`).replace(/\n/g, "\\n");
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Tadpole//Booking//EN",
    "BEGIN:VEVENT",
    `UID:${stamp(start)}-${Math.random().toString(36).slice(2)}@tadpole`,
    `DTSTAMP:${stamp(moment())}`,
    `DTSTART:${stamp(start)}`,
    `DTEND:${stamp(end)}`,
    `SUMMARY:${escape(event.title)}`,
    `DESCRIPTION:${escape(event.details)}`,
    `LOCATION:${escape(event.location)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .map(fold)
    .join("\r\n");
  return {
    google: `https://calendar.google.com/calendar/render?${google}`,
    apple: `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`,
    outlook: `https://outlook.live.com/calendar/0/deeplink/compose?${microsoft}`,
    office: `https://outlook.office.com/calendar/0/deeplink/compose?${microsoft}`,
  };
}
